import React from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText,
  Mail as MailIcon,
  MessageSquare,
  MessagesSquare,
  Paperclip,
  X,
  CalendarClock,
  ListTodo,
} from 'lucide-react';
import { Lead } from '../../../../types';
import { formatDateTimeDDMMYYYY } from '../../../../utils/date';
import { ACTIVITY_ICON_MAP } from '../../../../utils/activityIcons';
import { ModuleLink } from '../../../../components/ModuleLink';
import { InboxEmailProjectLinker } from '../../../inbox/components/InboxEmailProjectLinker';
import {
  getCcRecipients,
  mapApiLeadToInboxLeadOption,
  type LeadOption,
} from '../../../inbox/inboxShared';
import { inboxPath } from '../../../../utils/inboxRoutes';
import {
  formatLeadDocumentSize,
  getFileTypeStyle,
  getLeadDocumentUrl,
  isImageDocument,
  isLeadDocumentDeletable,
  isPdfDocument,
  leadDocumentHistoryTitle,
  type LeadDocument,
} from '../../../projects/utils/leadDocumentFiles';
import { AppConfirmDialog } from '../../../../components/AppConfirmDialog';
import { usePdfMailShare } from '../../../inbox/PdfMailShareProvider';
import { leadDocumentMailShareOptions } from '../../utils/documentMailDraft';
import { LeadHistoryEmailBody } from './LeadHistoryEmailBody';
import { ActivityNotesPanel } from './ActivityNotesPanel';
import { LeadNoteHistoryCard } from './LeadNoteHistoryCard';
import { LeadDocumentActionsMenu } from './LeadDocumentActionsMenu';
import type { HistoryFilter, LeadHistoryFeedEntry } from './useLeadHistoryEntries';
import { chatPath } from '../../../../utils/dashboardRoutes';
import { TaskAvatar, TaskCheckButton, TONE_TEXT_CLASS } from '../../../aufgaben/components/taskUi';
import { doneLabel, dueLabel, localYmd, memberName } from '../../../aufgaben/taskViews';
import type { Task, TaskMember } from '../../../aufgaben/types';

const HISTORY_FILTERS = [
  { id: 'all' as const, label: 'Alle' },
  { id: 'activities' as const, label: 'Aktivitäten' },
  { id: 'notes' as const, label: 'Notizen' },
  { id: 'emails' as const, label: 'E-Mails' },
  { id: 'files' as const, label: 'Dateien' },
  { id: 'chats' as const, label: 'KI-Chats' },
  { id: 'tasks' as const, label: 'To-dos' },
] as const;

export type LeadHistoryFilterCounts = {
  activities: number;
  notes: number;
  emails: number;
  files: number;
  chats: number;
  tasks: number;
};

type ActivityTypeOption = { id: string; label: string; icon: string };

/** Filterleiste + vereinheitlichter Verlaufs-Feed (Notizen/E-Mails/Dateien). Reiner Renderer — Aufbau der Einträge kommt aus `useLeadHistoryEntries`. */
export function LeadHistoryFeed({
  historyFilter,
  setHistoryFilter,
  filterCounts,
  emailLinkWarning,
  setEmailLinkWarning,
  lead,
  entries,
  noteHistoryMenuEntryId,
  setNoteHistoryMenuEntryId,
  togglePinnedNoteHistory,
  deleteNoteHistoryEntry,
  activityTypesList,
  expandedEmailId,
  setExpandedEmailId,
  canViewInbox,
  showEmailsTab = true,
  showChatsTab = false,
  showTasksTab = false,
  canEditTask,
  taskMembersById,
  onToggleTask,
  leadsForEmailLink,
  loadLeadEmails,
  hasMoreEmails = false,
  loadingMoreEmails = false,
  onLoadMoreEmails,
  setPreviewDocument,
  handleDeleteDocument,
}: {
  historyFilter: HistoryFilter;
  setHistoryFilter: React.Dispatch<React.SetStateAction<HistoryFilter>>;
  filterCounts: LeadHistoryFilterCounts;
  emailLinkWarning: string | null;
  setEmailLinkWarning: React.Dispatch<React.SetStateAction<string | null>>;
  lead: Lead;
  entries: LeadHistoryFeedEntry[];
  noteHistoryMenuEntryId: string | number | null;
  setNoteHistoryMenuEntryId: React.Dispatch<React.SetStateAction<string | number | null>>;
  togglePinnedNoteHistory: (id: string | number) => void | Promise<void>;
  deleteNoteHistoryEntry: (id: string | number) => void | Promise<void>;
  activityTypesList: ActivityTypeOption[];
  expandedEmailId: string | null;
  setExpandedEmailId: React.Dispatch<React.SetStateAction<string | null>>;
  canViewInbox: boolean;
  /** E-Mails-Filter nur mit Modul Posteingang. */
  showEmailsTab?: boolean;
  showChatsTab?: boolean;
  /** To-dos-Filter und -Karten ab Recht web.aufgaben.own */
  showTasksTab?: boolean;
  canEditTask?: (task: Task) => boolean;
  taskMembersById?: Map<string, TaskMember>;
  onToggleTask?: (task: Task) => void;
  leadsForEmailLink: ReturnType<typeof mapApiLeadToInboxLeadOption>[] | LeadOption[];
  loadLeadEmails: () => void;
  hasMoreEmails?: boolean;
  loadingMoreEmails?: boolean;
  onLoadMoreEmails?: () => void;
  setPreviewDocument: (doc: LeadDocument | null) => void;
  handleDeleteDocument: (docId: number) => void | Promise<void>;
}) {
  const { instanceId } = useParams<{ instanceId: string }>();
  const groupedEntries = entries;
  const visibleFilters = HISTORY_FILTERS.filter(
    (f) =>
      (f.id !== 'emails' || showEmailsTab) &&
      (f.id !== 'chats' || showChatsTab) &&
      (f.id !== 'tasks' || showTasksTab),
  );
  const today = localYmd();
  const mailShare = usePdfMailShare();
  const [deleteTarget, setDeleteTarget] = React.useState<LeadDocument | null>(null);

  const documentMenuProps = (doc: LeadDocument) => {
    const pdf = isPdfDocument(doc);
    return {
      filename: doc.filename,
      onOpen: () => setPreviewDocument(doc),
      printUrl: pdf ? getLeadDocumentUrl(lead.id, doc) : undefined,
      downloadUrl: getLeadDocumentUrl(lead.id, doc, { download: true }),
      onShareMail:
        pdf && mailShare ? () => mailShare.sharePdfByMail(leadDocumentMailShareOptions(lead.id, doc)) : undefined,
      onDelete: isLeadDocumentDeletable(doc) ? () => setDeleteTarget(doc) : undefined,
    };
  };

  const filterLabel = (id: (typeof HISTORY_FILTERS)[number]['id'], label: string) => {
    if (id === 'all') return label;
    const count = filterCounts[id];
    return count > 0 ? `${label} (${count})` : label;
  };

  const chatEntriesEmpty = historyFilter === 'chats' && !entries.some((e) => e.type === 'chat');
  const taskEntriesEmpty = historyFilter === 'tasks' && !entries.some((e) => e.type === 'task');

  return (
    <>
      {/* History Filter */}
      {emailLinkWarning ? (
        <div className="mt-4 sm:mt-8 mb-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm border border-amber-200 flex justify-between items-start gap-3">
          <span>{emailLinkWarning}</span>
          <button
            type="button"
            onClick={() => setEmailLinkWarning(null)}
            className="shrink-0 text-amber-700 hover:underline"
          >
            Schließen
          </button>
        </div>
      ) : null}
      <div className={`${emailLinkWarning ? 'mt-2' : 'mt-4 sm:mt-6'} mb-4`}>
        <div className="flex flex-wrap border-b border-app-border">
          {visibleFilters.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setHistoryFilter(id)}
              className={`flex-1 sm:flex-none px-2 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                historyFilter === id
                  ? 'text-pipedrive-blue border-pipedrive-blue'
                  : 'text-app-text-subtle border-transparent hover:text-app-text-muted'
              }`}
            >
              {filterLabel(id, label)}
            </button>
          ))}
        </div>
      </div>

      {/* Einträge als Karten: volle Breite (Alle / Notizen gefiltert) */}
      <div className="space-y-4 w-full pb-6">
        {groupedEntries.map((e) => {
          if (e.type === 'created') {
            return (
              <article key="created" className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg">
                <div className="w-10 h-10 rounded-full bg-app-surface-muted flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-app-text-muted" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-app-text">Projekt erstellt</h3>
                  <p className="text-xs text-app-text-subtle mt-0.5">
                    {formatDateTimeDDMMYYYY(lead.created_at)} · Erstellt von System
                  </p>
                  <p className="mt-2 text-sm text-app-text-muted">
                    Neues Projekt wurde im System angelegt. {lead.lead_title || 'Projekt'} von {lead.name}.
                  </p>
                </div>
              </article>
            );
          }
          if (e.type === 'note') {
            const menuOpen = noteHistoryMenuEntryId === e.id;
            const pinnedIds = (lead.pinned_note_history_ids ?? []).map(String);
            const isPinned = pinnedIds.includes(String(e.id));
            return (
              <LeadNoteHistoryCard
                key={`note-${e.id}`}
                title="Notiz erstellt"
                snippet={e.snippet || ''}
                savedAt={e.saved_at}
                authorLabel={e.author_label}
                isPinned={isPinned}
                pinnedCount={pinnedIds.length}
                menuOpen={menuOpen}
                onToggleMenu={() =>
                  setNoteHistoryMenuEntryId((prev) => (prev === e.id ? null : e.id))
                }
                onTogglePin={() => void togglePinnedNoteHistory(e.id)}
                onDelete={() => void deleteNoteHistoryEntry(e.id)}
              />
            );
          }
          if (e.type === 'activity') {
            const typeEntry = activityTypesList.find((t) => t.id === e.activity_type);
            const Icon = typeEntry ? (ACTIVITY_ICON_MAP[typeEntry.icon] ?? CalendarClock) : CalendarClock;
            const whenLabel = e.due_at
              ? formatDateTimeDDMMYYYY(e.due_at)
              : formatDateTimeDDMMYYYY(e.created_at);
            const activityNotes = String(e.notes || '').trim();
            const activityCreator = String(e.created_by_label || '').trim();
            return (
              <article key={`activity-${e.id}`} className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg">
                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-app-text">Aktivität geplant</h3>
                  <p className="text-xs text-app-text-subtle mt-0.5">
                    {whenLabel}
                    {activityCreator ? ` · Erstellt von ${activityCreator}` : ''}
                  </p>
                  <div className="mt-2.5">
                    <p className="text-sm font-medium text-app-text">{e.title}</p>
                    <p className="text-xs text-app-text-subtle mt-0.5">
                      Typ: {typeEntry?.label || e.activity_type}
                      {e.done ? ' · Erledigt' : ' · Offen'}
                    </p>
                    {activityNotes ? (
                      <ActivityNotesPanel
                        notes={activityNotes}
                        className="mt-2.5 rounded-lg bg-note-surface px-3 py-2.5"
                      />
                    ) : null}
                  </div>
                </div>
              </article>
            );
          }
          if (e.type === 'email') {
            const isExpanded = expandedEmailId === e.id;
            const toDisplay = String(e.to_addr || '')
              .split(/\s*\|\s*cc:/i)[0]
              .trim();
            const ccRecipients = getCcRecipients(e);
            return (
              <article
                key={`email-${e.id}`}
                className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg"
              >
                <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center shrink-0">
                  <MailIcon className="w-5 h-5 text-sky-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-medium text-app-text">{e.subject || '(Kein Betreff)'}</h3>
                      <p className="text-xs text-app-text-subtle mt-0.5 break-words">
                        {e.date_at ? formatDateTimeDDMMYYYY(e.date_at) : '—'}
                        {' · '}
                        Von: {e.from_addr || '—'}
                        {' · '}
                        An: {toDisplay || '—'}
                        {ccRecipients.length > 0 ? ` · Cc: ${ccRecipients.join(', ')}` : null}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      {canViewInbox ? (
                        <ModuleLink
                          to={inboxPath(e.id)}
                          className="p-1.5 rounded-md text-app-text-subtle hover:bg-app-hover hover:text-sky-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30"
                          title={
                            e.thread_id
                              ? 'E-Mail-Thread im Posteingang öffnen'
                              : 'E-Mail im Posteingang öffnen'
                          }
                          aria-label={
                            e.thread_id
                              ? 'E-Mail-Thread im Posteingang öffnen'
                              : 'E-Mail im Posteingang öffnen'
                          }
                        >
                          <MessagesSquare className="w-4 h-4" />
                        </ModuleLink>
                      ) : null}
                      {canViewInbox ? (
                        <InboxEmailProjectLinker
                          variant="menu"
                          emailId={e.id}
                          threadId={e.thread_id ?? null}
                          currentLeadId={e.lead_id ?? lead.id}
                          leads={leadsForEmailLink}
                          onLinked={(_leadId, warning) => {
                            loadLeadEmails();
                            setEmailLinkWarning(warning ?? null);
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                  <LeadHistoryEmailBody
                    emailId={e.id}
                    bodyHtml={e.body_html}
                    bodyText={e.body_text}
                    snippet={e.snippet}
                    hasMoreBody={e.has_more_body}
                    accountId={e.account_id}
                    externalMessageId={e.external_message_id}
                    attachmentsJson={e.attachments_json}
                    linkedDocuments={e.linkedDocuments}
                    onPreviewDocument={setPreviewDocument}
                    isExpanded={isExpanded}
                    onToggleExpanded={() =>
                      setExpandedEmailId((prev) => (prev === e.id ? null : e.id))
                    }
                  />
                </div>
              </article>
            );
          }
          if (e.type === 'file-group') {
            const docs = e.docs;
            return (
              <article key={`file-group-${docs[0].id}`} className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                  <Paperclip className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-app-text">
                    {leadDocumentHistoryTitle(docs)}
                  </h3>
                  <p className="text-xs text-app-text-subtle mt-0.5">
                    {formatDateTimeDDMMYYYY(docs[0].created_at)}
                    {docs[0].created_by_label ? ` · Erstellt von ${docs[0].created_by_label}` : ''}
                  </p>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                    {docs.map((doc) => {
                      const docUrl = getLeadDocumentUrl(lead.id, doc);
                      const isImg = isImageDocument(doc);
                      const typeStyle = getFileTypeStyle(doc.filename);
                      return (
                        <div
                          key={doc.id}
                          className="group relative rounded-lg border border-app-border bg-app-surface hover:border-app-border-strong transition-colors"
                        >
                          <button
                            type="button"
                            onClick={() => setPreviewDocument(doc)}
                            // Nur Fotos brauchen eine große Kachel; sonst steht dort nur das Typ-Label.
                            className={`block w-full bg-app-surface-muted overflow-hidden rounded-t-lg ${
                              isImg ? 'aspect-[4/3]' : 'h-14'
                            }`}
                            aria-label={`${doc.filename} öffnen`}
                          >
                            {isImg ? (
                              <img src={docUrl} alt={doc.filename} className="w-full h-full object-cover" />
                            ) : (
                              <span className={`flex w-full h-full items-center justify-center text-sm font-bold ${typeStyle.bg} ${typeStyle.text}`}>
                                {typeStyle.label}
                              </span>
                            )}
                          </button>
                          <LeadDocumentActionsMenu variant="overlay" {...documentMenuProps(doc)} />
                          <div className="p-2.5 sm:p-3">
                            <p className="text-sm font-medium text-app-text truncate" title={doc.filename}>
                              {doc.filename}
                            </p>
                            <p className="text-xs text-app-text-subtle mt-0.5">
                              {formatLeadDocumentSize(doc.size)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </article>
            );
          }
          if (e.type === 'file') {
            return (
              <article key={`file-${e.id}`} className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                  <Paperclip className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-medium text-app-text">{leadDocumentHistoryTitle([e])}</h3>
                      <p className="text-xs text-app-text-subtle mt-0.5">
                        {formatDateTimeDDMMYYYY(e.created_at)}
                        {e.created_by_label ? ` · Erstellt von ${e.created_by_label}` : ''}
                      </p>
                    </div>
                    <LeadDocumentActionsMenu {...documentMenuProps(e)} />
                  </div>
                  <div className="mt-2.5 flex min-w-0 items-baseline gap-2 text-sm">
                    <button
                      type="button"
                      onClick={() => setPreviewDocument(e)}
                      className="min-w-0 font-medium text-pipedrive-blue hover:underline truncate text-left"
                      title={e.filename}
                    >
                      {e.filename}
                    </button>
                    <span className="text-xs text-app-text-subtle shrink-0">{formatLeadDocumentSize(e.size)}</span>
                  </div>
                </div>
              </article>
            );
          }
          if (e.type === 'task') {
            const t = e.task;
            const assignee = t.assigned_user_id ? taskMembersById?.get(t.assigned_user_id) ?? null : null;
            const due = t.done ? { text: doneLabel(t, today), tone: 'default' as const } : dueLabel(t, today);
            const href = instanceId
              ? `/i/${encodeURIComponent(instanceId)}/todos?task=${encodeURIComponent(t.id)}`
              : null;
            return (
              <article
                key={`task-${t.id}`}
                className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg"
              >
                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                  <ListTodo className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-app-text">{t.done ? 'To-do erledigt' : 'To-do angelegt'}</h3>
                  <p className="text-xs text-app-text-subtle mt-0.5">
                    {formatDateTimeDDMMYYYY(e.date)} · <span className="font-mono">{t.display_id}</span>
                  </p>
                  <div className="mt-2.5 flex items-center gap-2.5">
                    <TaskCheckButton
                      done={t.done}
                      priority={t.priority}
                      disabled={!canEditTask?.(t) || !onToggleTask}
                      onToggle={() => onToggleTask?.(t)}
                    />
                    {href ? (
                      <ModuleLink
                        to={href}
                        className={`min-w-0 flex-1 truncate text-sm font-medium hover:underline ${
                          t.done ? 'text-app-text-subtle line-through' : 'text-app-text'
                        }`}
                      >
                        {t.title}
                      </ModuleLink>
                    ) : (
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-app-text">{t.title}</span>
                    )}
                    {due.text && (
                      <span className={`shrink-0 whitespace-nowrap text-xs font-medium ${TONE_TEXT_CLASS[due.tone]}`}>
                        {due.text}
                      </span>
                    )}
                    <span title={assignee ? memberName(assignee) : 'Nicht zugewiesen'}>
                      <TaskAvatar member={assignee} />
                    </span>
                  </div>
                </div>
              </article>
            );
          }
          if (e.type === 'chat') {
            const title = e.title || 'Neuer Chat';
            const href = instanceId ? chatPath(instanceId, e.chatId) : null;
            const inner = (
              <>
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                  <MessageSquare className="w-5 h-5 text-pipedrive-blue" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-app-text">{title}</h3>
                  <p className="text-xs text-app-text-subtle mt-0.5">{formatDateTimeDDMMYYYY(e.date)}</p>
                </div>
              </>
            );
            if (href) {
              return (
                <ModuleLink
                  key={`chat-${e.chatId}`}
                  to={href}
                  className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg hover:border-app-border-strong"
                >
                  {inner}
                </ModuleLink>
              );
            }
            return (
              <article key={`chat-${e.chatId}`} className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg">
                {inner}
              </article>
            );
          }
          return (
            <article key="lost" className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-app-surface border border-app-border rounded-lg">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <X className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-app-text">Lead als verloren markiert</h3>
                <p className="text-xs text-app-text-subtle mt-0.5">
                  {lead.lost_at
                    ? formatDateTimeDDMMYYYY(lead.lost_at)
                    : '—'}
                  {lead.lost_by ? ` · Markiert von ${lead.lost_by}` : ''}
                </p>
                <div className="mt-3 p-3 rounded-lg bg-app-surface-muted border border-app-border text-sm text-app-text-muted">
                  {lead.loss_reason && (
                    <p><span className="font-medium text-app-text-subtle">Verlustgrund:</span> {lead.loss_reason}</p>
                  )}
                  {lead.loss_comment && (
                    <p className="mt-1"><span className="font-medium text-app-text-subtle">Kommentar:</span> {lead.loss_comment}</p>
                  )}
                </div>
              </div>
            </article>
          );
        })}
        {chatEntriesEmpty ? (
          <p className="py-6 text-center text-sm text-app-text-subtle">Keine KI-Chats zu diesem Projekt.</p>
        ) : null}
        {taskEntriesEmpty ? (
          <p className="py-6 text-center text-sm text-app-text-subtle">
            Keine To-dos zu diesem Projekt. Verknüpfe ein To-do über „Anfrage verknüpfen".
          </p>
        ) : null}
      </div>
      {hasMoreEmails && onLoadMoreEmails ? (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={onLoadMoreEmails}
            disabled={loadingMoreEmails}
            className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm text-app-text-muted hover:bg-app-hover disabled:opacity-60"
          >
            {loadingMoreEmails ? 'E-Mails werden geladen…' : 'Weitere E-Mails laden'}
          </button>
        </div>
      ) : null}
      <AppConfirmDialog
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        title="Datei löschen?"
        description={deleteTarget ? `„${deleteTarget.filename}“ wird aus dem Projekt gelöscht.` : undefined}
        confirmLabel="Löschen"
        destructive
        onConfirm={async () => {
          const target = deleteTarget;
          setDeleteTarget(null);
          if (target) await handleDeleteDocument(target.id);
        }}
      />
    </>
  );
}
